import type { ApprovalMode, EmailStatus, FlowState } from "@/lib/types";
import { cn } from "@/lib/cn";

/**
 * Colour carries meaning, so the palette groups by outcome rather than by
 * lifecycle order: green means it reached someone, amber means it needs a
 * person, red means it did not arrive, grey means nothing has happened yet.
 * An operator scanning a long list reads the colour before the word.
 */
const emailStyles: Record<EmailStatus, string> = {
  DRAFT:        "bg-amber-50 text-amber-800 ring-1 ring-amber-200",
  QUEUED:       "bg-zinc-100 text-zinc-700",
  SENT:         "bg-blue-50 text-blue-700",
  DELIVERED:    "bg-emerald-50 text-emerald-700",
  OPENED:       "bg-teal-50 text-teal-700",
  REPLIED:      "bg-indigo-50 text-indigo-700",
  BOUNCED:      "bg-rose-50 text-rose-700",
  COMPLAINED:   "bg-rose-100 text-rose-800",
  FAILED:       "bg-rose-50 text-rose-700",
  UNSUBSCRIBED: "bg-zinc-100 text-zinc-600",
  REJECTED:     "bg-orange-50 text-orange-700",
};

const emailLabels: Partial<Record<EmailStatus, string>> = {
  // "DRAFT" alone reads like an unfinished note the operator left. It is in fact
  // a finished message the agent is blocked on, which is a call to action.
  DRAFT: "AWAITING APPROVAL",
};

export function EmailStatusBadge({ status }: { status: EmailStatus }) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap",
        emailStyles[status],
      )}
    >
      {emailLabels[status] ?? status}
    </span>
  );
}

/**
 * Marks an inbound message the ingest-time zero-trust screen flagged as a
 * possible prompt-injection attempt. The message was processed normally, but
 * the agent's response was forced into human review and the text was kept out
 * of knowledge retrieval — this badge tells the reviewer why.
 */
export function InjectionBadge() {
  return (
    <span
      title="This message matched prompt-injection heuristics. The agent's reply requires human approval and the text was excluded from knowledge retrieval."
      className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2 py-0.5 text-xs font-medium whitespace-nowrap text-rose-700 ring-1 ring-rose-200"
    >
      ⚠ possible injection
    </span>
  );
}

const flowStyles: Record<FlowState, string> = {
  NOT_STARTED: "bg-zinc-100 text-zinc-600",
  RUNNING:     "bg-emerald-50 text-emerald-700",
  PAUSED:      "bg-amber-50 text-amber-800",
  STOPPED:     "bg-zinc-100 text-zinc-500",
};

const flowLabels: Record<FlowState, string> = {
  NOT_STARTED: "Not started",
  RUNNING:     "Running",
  PAUSED:      "Paused",
  STOPPED:     "Stopped",
};

export function FlowStateBadge({ state }: { state: FlowState | null }) {
  if (!state) return <span className="text-xs text-[var(--fg-muted)]">—</span>;
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap",
        flowStyles[state],
      )}
    >
      {flowLabels[state]}
    </span>
  );
}

/**
 * What will happen to the next message the agent composes for this prospect.
 *
 * <p>Shown beside the prospect's status because it is the single fact that
 * decides whether this conversation moves on its own or waits for a person —
 * and the answer can come from either the strategy or a per-prospect override.
 */
export function ApprovalModeBadge({
  mode,
  overridden,
}: {
  mode: ApprovalMode;
  overridden: boolean;
}) {
  const review = mode === "REVIEW";
  return (
    <span
      title={
        overridden
          ? "Set on this prospect, overriding the strategy."
          : "Inherited from the strategy."
      }
      className={cn(
        "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap",
        review ? "bg-amber-50 text-amber-800 ring-1 ring-amber-200" : "bg-sky-50 text-sky-700",
      )}
    >
      {review ? "Review before send" : "Sends automatically"}
      {overridden ? " · override" : ""}
    </span>
  );
}
